import { Injectable } from '@angular/core';
import { EventTypes } from '../models/event';
import { BehaviorSubject } from 'rxjs';
import { RED_COLOR_HEX, DATA_QUALITY_COLORS, EDIT_COLOR, ORANGE_COLOR_HEX, ANNOTATION_MARKER_COLOR, ANNOTATION_IGNORED_MARKER_COLOR } from '../constant';
import { MarkerTypes } from '../models/hydrograph';

@Injectable()
export class HydrographNEWService {

    /** #14192 entity colors as observable */
    private entityColors$: BehaviorSubject<{[key: number]: string}> = new BehaviorSubject([]);

    public getMarkerTypes() {
        const markerTypes: MarkerTypes = {
            normal: {
                fillColor: undefined,
                symbol: undefined,
                radius: null,
                enabled: false,
                states: {
                    hover: {
                        symbol: 'diamond',
                        radius: 4,
                        enabled: true,
                        fillColor: undefined,
                    },
                },
            },
            marked: {
                fillColor: undefined,
                symbol: 'diamond',
                radius: 4,
                enabled: true,
            },
            flagged: {
                fillColor: RED_COLOR_HEX,
                symbol: 'diamond',
                radius: 4,
                enabled: true,
            },
            selected: {
                // TODO: Temporary fill color to be different then others, since LC color lines not points
                // fillColor: 'blue',
                fillColor: '#FF8C00',
                symbol: 'diamond',
                radius: 4,
                enabled: true,
            },
            snapped: {
                fillColor: EDIT_COLOR,
                symbol: 'diamond',
                radius: 4,
                enabled: true,
            },
            corrected: {
                fillColor: EDIT_COLOR,
                symbol: `diamond`,
                radius: 4,
                enabled: true,
            },
            correctedOnDragAndDrop: {
                fillColor: ORANGE_COLOR_HEX,
                symbol: `diamond`,
                radius: 6,
                enabledThreshold: 5,
                enabled: true,
            },
            interpolated: {
                fillColor: EDIT_COLOR,
                symbol: `diamond`,
                radius: 4,
                enabled: true,
            },
            annotation: {
                1: {
                    // Depth
                    fillColor: ANNOTATION_MARKER_COLOR,
                    symbol: `triangle`,
                    radius: 5,
                    enabled: true,
                },
                2: {
                    // Velocity
                    fillColor: ANNOTATION_MARKER_COLOR,
                    symbol: `circle`,
                    radius: 5,
                    enabled: true,
                },
                3: {
                    // Quantity or flow
                    fillColor: ANNOTATION_MARKER_COLOR,
                    symbol: `square`,
                    radius: 5,
                    enabled: true,
                },
                4: {
                    // Original (pre-edit) data
                    fillColor: ANNOTATION_MARKER_COLOR,
                    symbol: `circle`,
                    radius: 5,
                    enabled: true,
                },
            },
            flaggedAnnotation: {
                1: {
                    // Depth
                    fillColor: ANNOTATION_IGNORED_MARKER_COLOR,
                    symbol: `triangle`,
                    radius: 5,
                    enabled: true,
                },
                2: {
                    // Velocity
                    fillColor: ANNOTATION_IGNORED_MARKER_COLOR,
                    symbol: `circle`,
                    radius: 5,
                    enabled: true,
                },
                3: {
                    // Quantity or flow
                    fillColor: ANNOTATION_IGNORED_MARKER_COLOR,
                    symbol: `square`,
                    radius: 5,
                    enabled: true,
                }
            }
        };

        return markerTypes;
    }

    public getEventsTheme(eventType: EventTypes): { color: string, eventSymbol: string} {
        const out = {
            color: '',
            eventSymbol: ''
        }
        switch(eventType) {
            default:
            case EventTypes.General: out.color = '#4e7e5b'; break;
            case EventTypes.Rain: out.color = '#00a8f0';  break;
            case EventTypes.Hydraulic: out.color = '#fdd93b';  break;
            case EventTypes.SiteCondition: out.color = '#7e5b4e';  break;
            case EventTypes.WorkOrder: out.color = '#1de9b6';  break;
            case EventTypes.CSO: out.color = '#fb8c00'; break;
        }

        switch(eventType) {
            default:
            case EventTypes.General: out.eventSymbol = 'url(../../assets/images/event_general.png)'; break;
            case EventTypes.Rain: out.eventSymbol = 'url(../../assets/images/event_rain.png)'; break;
            case EventTypes.Hydraulic: out.eventSymbol = 'url(../../assets/images/event_hydraulic.png)'; break;
            case EventTypes.SiteCondition: out.eventSymbol = 'url(../../assets/images/event_sitecondition.png)'; break;
            case EventTypes.WorkOrder: out.eventSymbol = 'url(../../assets/images/event_workorder.png)'; break;
            case EventTypes.CSO: out.eventSymbol = 'url(../../assets/images/event_cso.png)'; break;
        }

        return out;
    }

    public getDataQualityColor(value: number) {
        return DATA_QUALITY_COLORS[this.getDataQualityIndex(value)];
    }

    public getDataQualityIndex(value: number): number {
        // Choose color based on percentage
        // If no value is passed in, set percentage to 100 (we assume quality is good)
        // Value is range from 0-15
        const percentage = value ? (value * 100) / 15 : 100;

        if (percentage < 11) {
            //  0 - 10 %
            return 0;
        } else if (percentage < 21) {
            //  11 - 20 %
            return 1;
        } else if (percentage < 31) {
            //  21 - 30 %
            return 2;
        } else if (percentage < 41) {
            //  31 - 40 %
            return 3;
        } else if (percentage < 51) {
            //  41 - 50 %
            return 4;
        } else if (percentage < 61) {
            //  51 - 60 %
            return 5;
        } else if (percentage < 71) {
            //  61 - 70 %
            return 6;
        } else if (percentage < 81) {
            //  71 - 80 %
            return 7;
        } else if (percentage < 91) {
            //  81 - 90 %
            return 8;
        } else if (percentage < 101) {
            //  91 - 100 %
            return 9;
        }

        return 9;
    }

    public entityColorsSubscription() {
        return this.entityColors$;
    }

    public entityColorGet(entityId: number): string {
        const colors = this.entityColors$.getValue();
        if(!colors || !colors[entityId]) {
            return '#aeaeae';
        }

        return this.entityColorParse(entityId, colors[entityId]);
    }

    public entityColorsSet(entityColors: {[key: number]: string}) {
        this.entityColors$.next(entityColors);
    }

    public entityColorParse(entityId: number, color: string) {
        if(entityId < 0) {
            return '#eb5910';
        }
        return color;
    }
}
